import { Modal, ScrollArea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { LicenceRecord, UserRole } from '@lms/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApiKeyStatus, updateApiKeyStatus } from '../api';
import { LicenceDetails } from './LicenceDetails';
import { showErrorToast, showSuccessToast } from './Toast';

interface LicenceDetailsModalProps {
  licence: LicenceRecord | null;
  opened: boolean;
  onClose: () => void;
  userRole?: UserRole;
  onUpdate?: (updates: Partial<LicenceRecord>) => void;
  onDelete?: () => void;
}

export const LicenceDetailsModal = ({ 
  licence, 
  opened, 
  onClose, 
  userRole, 
  onUpdate, 
  onDelete 
}: LicenceDetailsModalProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();
  
  const { data: apiKeyStatus } = useQuery({
    queryKey: ['apiKeyStatus', licence?.api_gw_key_id],
    queryFn: () => fetchApiKeyStatus(licence!.api_gw_key_id!),
    enabled: opened && !!licence?.api_gw_key_id,
  });

  const updateApiKeyMutation = useMutation({
    mutationFn: ({ apiKeyId, enabled }: { apiKeyId: string; enabled: boolean }) =>
      updateApiKeyStatus(apiKeyId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeyStatus', licence?.api_gw_key_id] });
      showSuccessToast('API key status updated');
    },
    onError: () => {
      showErrorToast('Failed to update API key status');
    },
  });

  const handleApiKeyStatusUpdate = (enabled: boolean) => {
    if (licence?.api_gw_key_id) {
      updateApiKeyMutation.mutate({ apiKeyId: licence.api_gw_key_id, enabled });
    }
  };
  
  if (!licence) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Licence Details - ${licence.customer_key}`}
      fullScreen={isMobile}
      size={isMobile ? undefined : "calc(100vw - 3rem)"}
      styles={isMobile ? undefined : {
        content: { height: 'calc(100vh - 3rem)' },
        body: { height: 'calc(100% - 60px)', overflow: 'auto' }
      }}
      centered={!isMobile}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <LicenceDetails
        licence={licence}
        userRole={userRole}
        onUpdate={onUpdate}
        onDelete={onDelete}
        apiKeyStatus={apiKeyStatus}
        onApiKeyStatusUpdate={handleApiKeyStatusUpdate}
      />
    </Modal>
  );
};
